import React from "react";
import { motion } from "framer-motion";
import { MapPin, Calendar, Clock, Download } from "lucide-react";
import {
    GoldDivider,
    CornerFlourish,
    GeometricPattern,
    Crescent,
} from "./Ornaments";
import { GoldParticles } from "./GoldParticles";
import { googleCalendarUrl, downloadIcs } from "../lib/calendar";

const ease = [0.22, 0.61, 0.36, 1];

const VenueBlock = ({ label, venueName, address, mapsUrl, testId, isDark }) => (
    <div className="flex flex-col items-center gap-3">
        {label && (
            <p
                className="tracking-editorial uppercase text-[10px]"
                style={{ color: isDark ? "#c9a95e" : "var(--gold-deep)" }}
            >
                {label}
            </p>
        )}
        <p
            className="font-serif-display text-xl md:text-2xl font-light"
            style={{ color: isDark ? "#f2e7cf" : "var(--charcoal)" }}
        >
            {venueName}
        </p>
        <p
            className="text-sm"
            style={{ color: isDark ? "#a89572" : "var(--muted)" }}
        >
            {address}
        </p>
        <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={testId}
            className={`${isDark ? "btn-outline-gold-dark" : "btn-outline-gold"} mt-2`}
        >
            <MapPin size={13} strokeWidth={1.5} />
            Open in Google Maps
        </a>
    </div>
);

export const EventSection = ({ event, variant }) => {
    const gcalUrl = googleCalendarUrl(event);

    // ambient overlays per variant
    const Ambient = () => {
        if (variant === "shukrana") {
            return (
                <>
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="lantern-glow absolute w-[520px] h-[520px] left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2" />
                        <div className="lantern-glow absolute w-[300px] h-[300px] left-[20%] top-[70%]" />
                        <div className="lantern-glow absolute w-[260px] h-[260px] right-[15%] top-[25%]" />
                    </div>
                    <GoldParticles count={18} />
                </>
            );
        }
        if (variant === "nikah") {
            return (
                <>
                    {/* Slowly rotating geometric star pattern */}
                    <motion.div
                        className="absolute inset-0 w-full h-full"
                        initial={{ opacity: 0, scale: 1.08 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 2.5, ease }}
                    >
                        <motion.div
                            className="w-full h-full"
                            animate={{ rotate: [0, 360] }}
                            transition={{
                                duration: 240,
                                repeat: Infinity,
                                ease: "linear",
                            }}
                            style={{ transformOrigin: "center" }}
                        >
                            <GeometricPattern
                                className="w-full h-full"
                                opacity={0.07}
                            />
                        </motion.div>
                    </motion.div>

                    {/* Soft radial golden light bloom */}
                    <motion.div
                        aria-hidden="true"
                        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full"
                        style={{
                            background:
                                "radial-gradient(circle, rgba(212,181,122,0.22) 0%, rgba(212,181,122,0.06) 40%, transparent 70%)",
                            filter: "blur(20px)",
                        }}
                        animate={{ opacity: [0.55, 0.85, 0.55], scale: [1, 1.05, 1] }}
                        transition={{
                            duration: 9,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                    />

                    {/* Calligraphy stroke reveal — top */}
                    <motion.svg
                        aria-hidden="true"
                        className="pointer-events-none absolute top-16 left-1/2 -translate-x-1/2 opacity-70"
                        width="380"
                        height="60"
                        viewBox="0 0 380 60"
                        fill="none"
                    >
                        <motion.path
                            d="M4 40 C 60 8, 120 8, 190 34 C 260 60, 320 60, 376 20"
                            stroke="#b89658"
                            strokeWidth="0.9"
                            strokeLinecap="round"
                            initial={{ pathLength: 0, opacity: 0 }}
                            whileInView={{ pathLength: 1, opacity: 1 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 3.2, ease, delay: 0.2 }}
                        />
                        <motion.circle
                            cx="190"
                            cy="34"
                            r="1.6"
                            fill="#b89658"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, delay: 2.4 }}
                        />
                    </motion.svg>

                    {/* Calligraphy stroke reveal — bottom, mirrored */}
                    <motion.svg
                        aria-hidden="true"
                        className="pointer-events-none absolute bottom-16 left-1/2 -translate-x-1/2 opacity-70"
                        width="380"
                        height="60"
                        viewBox="0 0 380 60"
                        fill="none"
                        style={{ transform: "translateX(-50%) rotate(180deg)" }}
                    >
                        <motion.path
                            d="M4 40 C 60 8, 120 8, 190 34 C 260 60, 320 60, 376 20"
                            stroke="#b89658"
                            strokeWidth="0.9"
                            strokeLinecap="round"
                            initial={{ pathLength: 0, opacity: 0 }}
                            whileInView={{ pathLength: 1, opacity: 1 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 3.2, ease, delay: 0.5 }}
                        />
                    </motion.svg>

                    {/* Floating crescents with gentle drift */}
                    <motion.div
                        aria-hidden="true"
                        className="absolute top-24 right-16"
                        initial={{ opacity: 0, rotate: -12, y: 8 }}
                        whileInView={{ opacity: 0.55, rotate: 0, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 3, ease }}
                    >
                        <motion.div
                            animate={{ y: [0, -10, 0], rotate: [0, 4, 0] }}
                            transition={{
                                duration: 12,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                        >
                            <Crescent size={80} />
                        </motion.div>
                    </motion.div>
                    <motion.div
                        aria-hidden="true"
                        className="absolute bottom-24 left-16"
                        initial={{ opacity: 0, rotate: 12, y: -8 }}
                        whileInView={{ opacity: 0.45, rotate: 0, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 3.5, ease }}
                    >
                        <motion.div
                            animate={{ y: [0, 10, 0], rotate: [0, -3, 0] }}
                            transition={{
                                duration: 14,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                        >
                            <Crescent size={60} />
                        </motion.div>
                    </motion.div>
                    <motion.div
                        aria-hidden="true"
                        className="absolute top-1/3 left-10 opacity-40"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 0.4 }}
                        viewport={{ once: true }}
                        transition={{ duration: 4, delay: 0.4 }}
                    >
                        <motion.div
                            animate={{ y: [0, 14, 0], rotate: [0, 6, 0] }}
                            transition={{
                                duration: 16,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                        >
                            <Crescent size={34} />
                        </motion.div>
                    </motion.div>
                    <motion.div
                        aria-hidden="true"
                        className="absolute bottom-1/3 right-10 opacity-40"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 0.35 }}
                        viewport={{ once: true }}
                        transition={{ duration: 4, delay: 0.7 }}
                    >
                        <motion.div
                            animate={{ y: [0, -12, 0], rotate: [0, -5, 0] }}
                            transition={{
                                duration: 18,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                        >
                            <Crescent size={44} />
                        </motion.div>
                    </motion.div>

                    {/* Delicate floating gold dust */}
                    <GoldParticles count={14} />
                </>
            );
        }
        if (variant === "valima") {
            return (
                <>
                    <div className="absolute inset-0 evening-haze pointer-events-none" />
                    <GoldParticles count={22} />
                </>
            );
        }
        return null;
    };

    const bg =
        variant === "shukrana"
            ? "linear-gradient(180deg, #1a1611 0%, #14100b 100%)"
            : variant === "nikah"
              ? "linear-gradient(180deg, #17130e 0%, #110e09 100%)"
              : "linear-gradient(180deg, #1a1611 0%, #0c0a07 100%)";

    const isDark = true; // whole site is dark luxury now
    const textPrimary = "#f2e7cf";
    const textSoft = "#d9c9a4";
    const textMuted = "#9a8b6b";

    return (
        <section
            id={event.id}
            data-testid={`event-section-${event.id}`}
            className="relative min-h-screen w-full flex items-center justify-center overflow-hidden paper-grain py-32"
            style={{ background: bg }}
        >
            <Ambient />

            <div className="relative z-10 mx-auto max-w-3xl w-full px-6 text-center">
                <CornerFlourish
                    className="absolute top-8 left-8 opacity-70"
                    rotate={0}
                />
                <CornerFlourish
                    className="absolute top-8 right-8 opacity-70"
                    rotate={90}
                />
                <CornerFlourish
                    className="absolute bottom-8 right-8 opacity-70"
                    rotate={180}
                />
                <CornerFlourish
                    className="absolute bottom-8 left-8 opacity-70"
                    rotate={270}
                />

                {variant === "nikah" && (
                    <>
                        <motion.p
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.4, ease }}
                            data-testid="bismillah-arabic"
                            className="font-arabic text-3xl md:text-4xl text-[color:var(--gold-deep)] mb-3"
                            dir="rtl"
                            lang="ar"
                        >
                            بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
                        </motion.p>
                        <motion.p
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.2, delay: 0.3 }}
                            className="mb-8 font-serif-display italic text-xs md:text-sm text-[color:var(--muted)]"
                        >
                            In the name of Allah, the Most Gracious, the Most Merciful
                        </motion.p>
                    </>
                )}

                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, ease }}
                    className="tracking-editorial uppercase text-[10px]"
                    style={{ color: isDark ? "#c9a95e" : "var(--gold-deep)" }}
                >
                    {event.day}
                </motion.p>

                <motion.h2
                    initial={{ opacity: 0, y: 20, filter: "blur(6px)" }}
                    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.6, ease, delay: 0.1 }}
                    className="font-serif-display mt-4 text-5xl md:text-7xl font-light"
                    style={{ color: textPrimary }}
                >
                    {event.title}
                </motion.h2>

                <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, delay: 0.4 }}
                    className="font-serif-display italic mt-3 text-lg md:text-xl"
                    style={{ color: textSoft }}
                >
                    {event.subtitle}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.55 }}
                    className="mt-8"
                >
                    <GoldDivider />
                </motion.div>

                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, delay: 0.7 }}
                    className="mt-10 max-w-xl mx-auto font-serif-display italic text-base md:text-lg leading-relaxed"
                    style={{ color: textSoft }}
                >
                    {event.description}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, delay: 0.85 }}
                    className="mt-14 flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16"
                >
                    <div className="flex items-center gap-3">
                        <Calendar
                            size={16}
                            strokeWidth={1.2}
                            style={{
                                color: isDark
                                    ? "#c9a95e"
                                    : "var(--gold-deep)",
                            }}
                        />
                        <span
                            className="tracking-[0.24em] uppercase text-xs"
                            style={{ color: textPrimary }}
                        >
                            {event.date}
                        </span>
                    </div>
                    <div
                        className="hidden md:block h-4 w-px"
                        style={{ background: "rgba(184,150,88,0.45)" }}
                    />
                    <div className="flex items-center gap-3">
                        <Clock
                            size={16}
                            strokeWidth={1.2}
                            style={{
                                color: isDark
                                    ? "#c9a95e"
                                    : "var(--gold-deep)",
                            }}
                        />
                        <span
                            className="tracking-[0.24em] uppercase text-xs"
                            style={{ color: textPrimary }}
                        >
                            {event.time}
                        </span>
                    </div>
                </motion.div>

                {/* Venue block(s) */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.3, delay: 1 }}
                    className="mt-16"
                    style={{ color: textPrimary }}
                >
                    {variant === "nikah" ? (
                        <div className="flex flex-col items-center gap-14">
                            {/* Highlighted Nikah venue — the Masjid */}
                            <div className="relative w-full max-w-xl mx-auto">
                                <div className="absolute -inset-x-6 -inset-y-8 md:-inset-x-10 md:-inset-y-10 border border-[color:var(--gold)]/45 pointer-events-none" />
                                <div className="absolute -inset-x-4 -inset-y-6 md:-inset-x-8 md:-inset-y-8 border border-[color:var(--gold)]/20 pointer-events-none" />
                                <span
                                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 tracking-editorial uppercase text-[10px] text-[color:var(--gold-deep)]"
                                    style={{ backgroundColor: "#14110c" }}
                                >
                                    The Nikah Ceremony
                                </span>
                                <div className="relative flex flex-col items-center gap-3 py-6">
                                    <p
                                        className="font-serif-display text-2xl md:text-3xl font-light text-[color:var(--charcoal)]"
                                        data-testid="nikah-masjid-name"
                                    >
                                        {event.venueName}
                                    </p>
                                    <p className="text-sm text-[color:var(--muted)]">
                                        {event.venueAddress}
                                    </p>
                                    <a
                                        href={event.mapsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="nikah-maps-masjid"
                                        className="btn-outline-gold mt-3"
                                    >
                                        <MapPin size={13} strokeWidth={1.5} />
                                        Open in Google Maps
                                    </a>
                                </div>
                            </div>

                            <p className="font-serif-display italic text-sm md:text-base text-[color:var(--muted)] tracking-wide">
                                Followed by lunch
                            </p>

                            <div className="grid md:grid-cols-2 gap-14 md:gap-8 w-full">
                                <VenueBlock
                                    label="Gent's"
                                    venueName={event.gents.venueName}
                                    address={event.gents.address}
                                    mapsUrl={event.gents.mapsUrl}
                                    testId="nikah-maps-gents"
                                    isDark={isDark}
                                />
                                <VenueBlock
                                    label="Ladies"
                                    venueName={event.ladies.venueName}
                                    address={event.ladies.address}
                                    mapsUrl={event.ladies.mapsUrl}
                                    testId="nikah-maps-ladies"
                                    isDark={isDark}
                                />
                            </div>
                        </div>
                    ) : (
                        <VenueBlock
                            venueName={event.venueName}
                            address={event.venueAddress}
                            mapsUrl={event.mapsUrl}
                            testId={`${event.id}-maps-button`}
                            isDark={isDark}
                        />
                    )}
                </motion.div>

                {/* Calendar buttons */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.3, delay: 1.2 }}
                    className="mt-14 flex flex-wrap items-center justify-center gap-4"
                >
                    <a
                        href={gcalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`${event.id}-gcal-button`}
                        className={isDark ? "btn-outline-gold-dark" : "btn-outline-gold"}
                    >
                        <Calendar size={13} strokeWidth={1.5} />
                        Add to Google Calendar
                    </a>
                    <button
                        onClick={() => downloadIcs(event)}
                        data-testid={`${event.id}-ics-button`}
                        className={isDark ? "btn-outline-gold-dark" : "btn-outline-gold"}
                    >
                        <Download size={13} strokeWidth={1.5} />
                        Add to Apple Calendar
                    </button>
                </motion.div>
            </div>
        </section>
    );
};

export default EventSection;
